'use client';

import { useTranslations } from 'next-intl';
import { Check, PlayCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRouter } from '@/i18n/routing';
import type { Registration } from '@/features/registration';

interface ReviewCompactRowProps {
  registration: Registration;
}

const STAGE_STYLES: Record<string, string> = {
  review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  ready_for_packing:
    'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-200'
};

const REVIEW_STAGE_STATUS_STYLES: Record<string, string> = {
  ready_for_review: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  returned_for_review:
    'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  reviewed:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
};

export function ReviewCompactRow({ registration }: ReviewCompactRowProps) {
  const t = useTranslations('review');
  const tc = useTranslations('common');
  const router = useRouter();

  const stage = registration.current_stage;
  const isCompleted = registration.review_completed_at != null;

  const stageLabels: Record<string, string> = {
    sticking: tc('stageSticking'),
    review: tc('stageReview'),
    ready_for_packing: tc('stageReadyForPacking')
  };
  const stageStatusLabels: Record<string, string> = {
    ready_for_review: t('subStatusReadyForReview'),
    returned_for_review: t('subStatusReturnedForReview'),
    reviewed: t('subStatusReviewed')
  };
  const subStatus = registration.stage_status ?? null;

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Container number */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            className='text-primary truncate font-medium hover:underline'
            onClick={() =>
              router.push(
                `/dashboard/containers/${registration.id}?stage=review`
              )
            }
          >
            {registration.container_number}
          </button>
        </div>
      </div>

      {/* Batch info (hidden on small screens) */}
      {/* {registration.latest_batch_id && (
        <div className='hidden items-center gap-1.5 md:flex'>
          <span className='text-muted-foreground text-sm'>
            {t('firstPage')}: {registration.latest_batch_start_page ?? '—'}
          </span>
          <span className='text-muted-foreground text-sm'>
            {t('lastPage')}: {registration.latest_batch_end_page ?? '—'}
          </span>
          <Badge
            variant='secondary'
            className='bg-green-100 px-1.5 py-0 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200'
          >
            <Check className='me-0.5 h-2.5 w-2.5' />
            {t('saved')}
          </Badge>
        </div>
      )} */}

      {/* Stage badge */}
      {stage !== 'archived' && (
        <Badge
          variant='secondary'
          className={cn('shrink-0 text-xs', STAGE_STYLES[stage] ?? '')}
        >
          {stageLabels[stage] ?? stage}
        </Badge>
      )}
      {subStatus && stageStatusLabels[subStatus] && (
        <Badge
          variant='secondary'
          className={cn(
            'shrink-0 text-xs',
            REVIEW_STAGE_STATUS_STYLES[subStatus] ?? ''
          )}
        >
          {stageStatusLabels[subStatus]}
        </Badge>
      )}

      {/* Action button */}
      <div className='flex shrink-0 items-center'>
        {isCompleted ? (
          <Button
            variant='outline'
            size='sm'
            className='h-7 text-xs'
            onClick={() => router.push(`/dashboard/review/${registration.id}`)}
          >
            {t('viewContainer')}
          </Button>
        ) : (
          <Button
            variant='default'
            size='sm'
            className='h-7 bg-green-600 text-xs text-white hover:bg-green-700'
            onClick={() => router.push(`/dashboard/review/${registration.id}`)}
          >
            <PlayCircle className='me-1 h-3.5 w-3.5' />
            {t('startReview')}
          </Button>
        )}
      </div>
    </div>
  );
}
